import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { OtaTagsDialog } from "src/ui/containers/dashboard/rollouts/components/VersionTable/OtaTagsDialog";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { getOtaFileReleaseNotes, hasOtaFileReleaseNotes } from "@dashboard/rollouts/utils/otaFileReleaseNotes";
import { ChangesDialog } from "@dashboard/shared/components/ChangesDialog";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import type { Row } from "@tanstack/react-table";

import { OtaFile } from "../../types";

const useStyles = makeStyles()((theme) => ({
    actionContainer: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}));

type Props = {
    row: Row<OtaFile>;
};

export const VersionTableActionsCell = ({ row }: Props) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [isChangesDialogOpen, setIsChangesDialogOpen] = React.useState(false);
    const [isTagsDialogOpen, setIsTagsDialogOpen] = React.useState(false);
    const { t } = useTranslation(["pages", "modals"]);
    const otaFile = row.original;
    const releaseNotes = getOtaFileReleaseNotes(otaFile);
    const hasReleaseNotes = hasOtaFileReleaseNotes(releaseNotes);
    const isDeleted = otaFile.status === "deleted";

    return (
        <MinimalTableCell style={{ maxWidth: 150 }}>
            <div className={classes.actionContainer}>
                <PrimaryButton
                    tooltipProps={{
                        title: isDeleted
                            ? t("pages:rolloutsPage.tooltips.startRolloutDisabled")
                            : t("pages:rolloutsPage.tooltips.startRollout"),
                    }}
                    onClick={() =>
                        dispatch(showDetails({ type: "create-rollout", selectedId: otaFile.fileId, codename: otaFile.codename }))
                    }
                    disabled={isDeleted}
                >
                    <i className="fa-solid fa-paper-plane" />
                </PrimaryButton>
                <SecondaryButton
                    tooltipProps={{ title: t("pages:rolloutsPage.tooltips.manageTags") }}
                    onClick={() => setIsTagsDialogOpen(true)}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>
                <SecondaryButton
                    tooltipProps={{
                        title: hasReleaseNotes
                            ? t("pages:downloadsPage.tooltips.showChanges")
                            : t("pages:downloadsPage.tooltips.showChangesDisabled"),
                    }}
                    onClick={() => setIsChangesDialogOpen(true)}
                    disabled={!hasReleaseNotes}
                >
                    <i className="fas fa-list" />
                </SecondaryButton>
            </div>
            <ChangesDialog
                title={t("modals:changesDialog.titles.rollout")}
                onClose={() => setIsChangesDialogOpen(false)}
                changesWithDate={releaseNotes}
                open={isChangesDialogOpen}
            />
            <OtaTagsDialog
                open={isTagsDialogOpen}
                onClose={() => setIsTagsDialogOpen(false)}
                codename={otaFile.codename}
                version={otaFile.version}
                otaTags={otaFile.annotation}
            />
        </MinimalTableCell>
    );
};
